import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";

import Loader from "../Loader/loader";
import Pagination from "../Pagenation/Pagenation";

import CashEntryServices from "../../../services/CashEntryServices";
import CashEntryAttachments from "./CashEntryAttachments";

import {
  activeCashDates,
  cashDate,
  cashError,
  cashFileUrl,
  cashId,
  cashLocalDateTime,
  cashMoney,
  cashRole,
  isAllowedCashDate,
  readCashData,
  useCashEntryDateWindow,
  validateCashFiles
} from "./cashEntryWorkflowUtils";

const WorkflowLoader = () => (
  <div
    className="d-flex justify-content-center align-items-center w-100"
    style={{ minHeight: 240 }}
    role="status"
    aria-label="Loading"
  >
    <Loader />
  </div>
);

const FileLinks = ({
  files = [],
  onRemove,
  disabled = false
}) => (
  <div className="d-grid gap-1">
    {!files.length && (
      <span className="text-muted">No attachments</span>
    )}

    {files.map((name, index) => (
      <div
        key={`${name}-${index}`}
        className="d-flex gap-2 align-items-center"
      >
        <a
          href={cashFileUrl(name)}
          target="_blank"
          rel="noopener noreferrer"
          style={{ overflowWrap: "anywhere" }}
        >
          {/\.pdf$/i.test(name) ? "PDF" : "Image"}: {name}
        </a>

        {onRemove && (
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            disabled={disabled}
            onClick={() => onRemove(index)}
          >
            Remove
          </button>
        )}
      </div>
    ))}
  </div>
);

// Existing callers use one-based pages.
// The shared Pagination component uses zero-based pages.
const Pager = ({
  page,
  total,
  limit = 20,
  disabled = false,
  onChange
}) => {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const first = total > 0 ? (page - 1) * limit + 1 : 0;
  const last = Math.min(page * limit, total);

  return (
    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
      <div className="small text-muted">
        Showing {first} to {last} of {total} requests
      </div>

      {total > 0 && (
        <Pagination
          activePage={page - 1}
          totalPages={totalPages}
          onPageChange={(nextPage) => {
            if (disabled || !Number.isInteger(nextPage)) return;

            onChange(
              Math.max(
                1,
                Math.min(nextPage + 1, totalPages)
              )
            );
          }}
        />
      )}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    pendingAccounts: "bg-warning text-dark",
    approved: "bg-success",
    rejected: "bg-danger"
  };

  const labels = {
    pendingAccounts: "Pending Accounts",
    approved: "Approved",
    rejected: "Rejected"
  };

  return (
    <span className={`badge ${styles[status] || "bg-secondary"}`}>
      {labels[status] || status}
    </span>
  );
};

export const CashEntryDateField = ({
  inputId,
  id,
  value = "",
  disabled = false,
  dateWindow,
  onChange,
  originalValue = "",
  horizontal = false
}) => {
  const fieldId = inputId || id || "cash-entry-date";
  const grants = activeCashDates(dateWindow);

  const original = Boolean(
    originalValue && value === originalValue
  );

  const dateValue = value.slice(0, 10);
  const timeValue = value.slice(11, 16);

  const grant = grants.find(
    (item) => item.date === dateValue
  );

  const mode = original
    ? "original"
    : grant
      ? grant.date
      : "normal";

  const minValue = original
    ? ""
    : grant?.min || dateWindow?.min || "";

  const maxValue = original
    ? ""
    : grant?.max || dateWindow?.max || "";

  const controlsDisabled =
    disabled || (!dateWindow && !original);

  const changeDate = (nextDate) => {
    if (controlsDisabled || original) return;

    if (!nextDate) {
      onChange("");
      return;
    }

    onChange(`${nextDate}T${timeValue || "00:00"}`);
  };

  const changeTime = (nextTime) => {
    if (controlsDisabled || original) return;

    const selectedDate =
      dateValue ||
      dateWindow?.defaultDateTime?.slice(0, 10);

    if (!selectedDate) return;

    onChange(`${selectedDate}T${nextTime}`);
  };

  return (
    <div className="w-100">
      {(grants.length > 0 || originalValue) && (
        <div className="mb-2">
          <label
            htmlFor={`${fieldId}-access`}
            className="form-label"
          >
            Allowed Date
          </label>

          <select
            id={`${fieldId}-access`}
            className="form-select"
            disabled={disabled || !dateWindow}
            value={mode}
            onChange={(event) => {
              const selected = event.target.value;

              if (selected === "original") {
                onChange(originalValue);
              } else if (selected === "normal") {
                onChange(
                  dateWindow?.defaultDateTime || ""
                );
              } else {
                onChange(
                  `${selected}T${timeValue || "00:00"}`
                );
              }
            }}
          >
            {originalValue && (
              <option value="original">
                Keep original date and time
              </option>
            )}

            <option value="normal">
              Today / Yesterday
            </option>

            {grants.map((item) => (
              <option
                key={item.grantId || item.date}
                value={item.date}
              >
                {item.date} — Accounts access
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Date and Time share one row when horizontal is enabled */}
      <div className={horizontal ? "row g-2" : ""}>
        <div className={horizontal ? "col-6" : "mb-2"}>
          <label
            htmlFor={fieldId}
            className="form-label"
          >
            Date (India)
          </label>

          <input
            id={fieldId}
            type="date"
            className="form-control"
            value={dateValue}
            min={minValue.slice(0, 10) || undefined}
            max={maxValue.slice(0, 10) || undefined}
            required
            readOnly={original}
            disabled={controlsDisabled}
            onChange={(event) =>
              changeDate(event.target.value)
            }
          />
        </div>

        <div className={horizontal ? "col-6" : ""}>
          <label
            htmlFor={`${fieldId}-time`}
            className="form-label"
          >
            Time (India)
          </label>

          <input
            id={`${fieldId}-time`}
            type="time"
            className="form-control"
            value={timeValue}
            min={
              dateValue === minValue.slice(0, 10)
                ? minValue.slice(11, 16) || undefined
                : undefined
            }
            max={
              dateValue === maxValue.slice(0, 10)
                ? maxValue.slice(11, 16) || undefined
                : undefined
            }
            step="60"
            required
            readOnly={original}
            disabled={controlsDisabled}
            onChange={(event) =>
              changeTime(event.target.value)
            }
          />
        </div>
      </div>

      <div className="small text-muted mt-1">
        {original
          ? "Original date retained. Select another option to change it."
          : grant
            ? `Access expires: ${cashDate(grant.expiresAt)}`
            : "Today or yesterday; other dates require Accounts access."}
      </div>
    </div>
  );
};

export const CashDateAccessPanel = ({
  passbookId,
  disabled = false,
  onRefresh,
  embedded = false
}) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const lock = useRef(false);
  const id = cashId(passbookId);
  const limit = 10;
  const visible = embedded || open;

  useEffect(() => {
    setPage(1);
    setRecords([]);
    setTotal(0);
    setDate("");
    setReason("");
    setError("");
  }, [id]);

  useEffect(() => {
    if (!visible || !id) return;

    let cancelled = false;

    setLoading(true);
    setError("");

    CashEntryServices.getDateAccessRequests({
      passbookId: id,
      page,
      limit
    })
      .then((response) => {
        if (cancelled) return;

        const data = readCashData(response);

        if (!Array.isArray(data?.dateAccessRequests)) {
          throw new Error("Invalid date-access response.");
        }

        const count = Number(data.total || 0);
        const lastPage = Math.max(
          1,
          Math.ceil(count / limit)
        );

        setTotal(count);

        if (page > lastPage) {
          setPage(lastPage);
          return;
        }

        setRecords(data.dateAccessRequests);
      })
      .catch((failure) => {
        if (cancelled) return;

        setRecords([]);
        setTotal(0);
        setError(cashError(failure));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [visible, id, page, refresh]);

  const submit = async () => {
    if (lock.current || disabled || !id) return;

    if (!date || !reason.trim()) {
      await Swal.fire(
        "Required",
        "Select a date and enter the reason.",
        "warning"
      );
      return;
    }

    lock.current = true;
    setBusy(true);

    try {
      await CashEntryServices.requestDateAccess({
        passbookId: id,
        allowedDate: date,
        reason: reason.trim()
      });

      setDate("");
      setReason("");
      setPage(1);
      setRefresh((value) => value + 1);
      onRefresh?.();

      await Swal.fire(
        "Submitted",
        "Date access request sent to Accounts.",
        "success"
      );
    } catch (failure) {
      await Swal.fire(
        "Error",
        cashError(failure),
        "error"
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  return (
    <div
      className={
        embedded
          ? "w-100"
          : "border rounded p-3 mb-3 w-100"
      }
    >
      {!embedded && (
        <button
          type="button"
          className="btn btn-outline-primary"
          disabled={!id || disabled || busy}
          onClick={() => setOpen((value) => !value)}
        >
          Other Date Access / History
        </button>
      )}

      {visible && (
        <div className={embedded ? "" : "mt-3"}>
          <div
            className="p-3 mb-3"
            style={{
              border: "1px solid #e5e9f0",
              borderRadius: 10,
              background: "#f5f7fb"
            }}
          >
            <div className="fw-semibold mb-3">
              Request Another Entry Date
            </div>

            <div className="row g-3">
              <div className="col-12 col-md-4">
                <label
                  htmlFor={`date-access-date-${id}`}
                  className="form-label"
                >
                  Required Date
                </label>

                <input
                  id={`date-access-date-${id}`}
                  type="date"
                  className="form-control"
                  value={date}
                  disabled={disabled || busy}
                  onChange={(event) =>
                    setDate(event.target.value)
                  }
                />
              </div>

              <div className="col-12 col-md-8">
                <label
                  htmlFor={`date-access-reason-${id}`}
                  className="form-label"
                >
                  Request Reason
                </label>

                <textarea
                  id={`date-access-reason-${id}`}
                  className="form-control"
                  rows={2}
                  placeholder="Explain why this date is required"
                  value={reason}
                  disabled={disabled || busy}
                  onChange={(event) =>
                    setReason(event.target.value)
                  }
                />
              </div>

              <div className="col-12 d-flex justify-content-center">
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  disabled={
                    disabled ||
                    busy ||
                    !id ||
                    !date ||
                    !reason.trim()
                  }
                  onClick={submit}
                >
                  {busy
                    ? "Submitting..."
                    : "Request 24-hour Access"}
                </button>
              </div>
            </div>

            <div className="small text-muted text-center mt-2">
              Accounts approval opens the requested date
              for 24 hours.
            </div>
          </div>

          <div className="fw-semibold mb-2">
            Date Access History
          </div>

          {loading ? (
            <WorkflowLoader />
          ) : error ? (
            <div className="alert alert-danger">
              {error}
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table
                  className="table table-bordered align-middle w-100 mb-0"
                  style={{ fontSize: 13 }}
                >
                  <thead className="table-light">
                    <tr>
                      <th>Required Date</th>
                      <th>Status</th>
                      <th>Access Expires</th>
                      <th>Reason / Accounts Remark</th>
                    </tr>
                  </thead>

                  <tbody>
                    {records.map((record) => {
                      const active =
                        record.status === "approved" &&
                        new Date(
                          record.expiresAt
                        ).getTime() > Date.now();

                      return (
                        <tr key={record._id}>
                          <td className="text-nowrap">
                            {record.allowedDate}
                          </td>

                          <td>
                            {record.status === "approved" ? (
                              <span
                                className={`badge ${active
                                  ? "bg-success"
                                  : "bg-secondary"
                                  }`}
                              >
                                {active ? "Active" : "Expired"}
                              </span>
                            ) : (
                              <StatusBadge
                                status={record.status}
                              />
                            )}
                          </td>

                          <td>
                            {cashDate(record.expiresAt)}
                          </td>

                          <td
                            style={{
                              whiteSpace: "pre-wrap",
                              overflowWrap: "anywhere"
                            }}
                          >
                            <div>
                              {record.reason || "—"}
                            </div>

                            {record.reviewRemark && (
                              <div className="text-muted mt-1">
                                <strong>Accounts:</strong>{" "}
                                {record.reviewRemark}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}

                    {!records.length && (
                      <tr>
                        <td
                          colSpan={4}
                          className="text-center text-muted py-4"
                        >
                          No date-access requests.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <Pager
                page={page}
                total={total}
                limit={limit}
                disabled={loading || busy}
                onChange={setPage}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

const AmendmentComparison = ({ record }) => (
  <div>
    <div className="table-responsive">
      <table className="table table-sm table-bordered w-100">
        <thead className="table-light">
          <tr>
            <th style={{ width: "20%" }}>Field</th>
            <th style={{ width: "40%" }}>Old</th>
            <th style={{ width: "40%" }}>Proposed / Approved</th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <th>Date &amp; Time</th>
            <td>{cashDate(record.oldValues?.dateTime)}</td>
            <td>{cashDate(record.newValues?.dateTime)}</td>
          </tr>
          <tr>
            <th>Amount</th>
            <td>{cashMoney(record.oldValues?.amount)}</td>
            <td>{cashMoney(record.newValues?.amount)}</td>
          </tr>
          <tr>
            <th>Remark</th>
            <td
              style={{
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere"
              }}
            >
              {record.oldValues?.remark}
            </td>
            <td
              style={{
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere"
              }}
            >
              {record.newValues?.remark}
            </td>
          </tr>
          <tr>
            <th>Attachments</th>
            <td>
              <FileLinks files={record.oldValues?.image} />
            </td>
            <td>
              <FileLinks files={record.newValues?.image} />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
      <strong>Reason:</strong> {record.reason}
    </div>

    <div>
      <strong>Balance change:</strong>{" "}
      {cashMoney(record.balanceChange)}
    </div>

    {record.status === "approved" && (
      <div>
        Executed balance: {cashMoney(record.balanceBefore)}
        {" → "}
        {cashMoney(record.balanceAfter)}
      </div>
    )}

    <div className="small text-muted mt-2">
      Requested: {record.requestedBy?.name || "—"}
      {" / "}
      {cashDate(record.createdAt)}
    </div>

    {record.reviewedAt && (
      <div className="small text-muted">
        Reviewed: {record.reviewedBy?.name || "—"}
        {" / "}
        {cashDate(record.reviewedAt)}
        {" / "}
        {record.reviewRemark || "—"}
      </div>
    )}
  </div>
);

export const CashAccountsWorkflow = ({
  onChanged,
  standalone = false
}) => {
  const [open, setOpen] = useState(standalone);
  const [kind, setKind] = useState("amendments");
  const [status, setStatus] = useState("pendingAccounts");
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState("");

  const lock = useRef(false);
  const role = cashRole();
  const isAccounts = role === "accounts";

  const canView = [
    "cashmaker",
    "cashchecker",
    "accounts"
  ].includes(role);

  const [entryType, setEntryType] = useState("out");

  const visible = standalone || open;
  const columns = kind === "amendments" ? 10 : 9;

  useEffect(() => {
    if (!visible || !canView) return;

    let cancelled = false;

    setLoading(true);
    setError("");
    setRecords([]);
    setExpandedId("");

    const load = async () => {
      try {
        const filters = { status, page, limit };

        // Maker/Checker: Cash Out / Cash In tabs.
        // Accounts retains its existing combined list.
        if (!isAccounts && kind === "amendments") {
          filters.entryType = entryType;
        }

        const response =
          kind === "amendments"
            ? await CashEntryServices.getAmendments(filters)
            : await CashEntryServices.getDateAccessRequests(filters);

        if (cancelled) return;

        const data = readCashData(response);
        const items =
          kind === "amendments"
            ? data?.amendments
            : data?.dateAccessRequests;

        if (!Array.isArray(items)) {
          throw new Error("Invalid workflow response.");
        }

        const count = Number(data.total || 0);

        if (!Number.isFinite(count) || count < 0) {
          throw new Error("Invalid request count.");
        }

        const lastPage = Math.max(1, Math.ceil(count / limit));
        setTotal(count);

        if (page > lastPage) {
          setPage(lastPage);
          return;
        }

        setRecords(items);
      } catch (failure) {
        if (!cancelled) {
          setRecords([]);
          setTotal(0);
          setError(cashError(failure));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    visible,
    canView,
    isAccounts,
    kind,
    status,
    page,
    limit,
    refresh,
    entryType
  ]);

  const review = async (record, decision) => {
    if (!isAccounts || lock.current || busy || loading) {
      return;
    }

    lock.current = true;
    setBusy(true);

    try {
      const result = await Swal.fire({
        title: `${decision === "approve" ? "Approve" : "Reject"
          } ${kind === "amendments" ? "Amendment" : "Date Access"
          }?`,
        text:
          kind === "amendments"
            ? `Balance change: ${cashMoney(record.balanceChange)}`
            : `${record.allowedDate}: approval opens this date for 24 hours.`,
        input: "textarea",
        inputPlaceholder: "Accounts remark",
        showCancelButton: true,
        confirmButtonText:
          decision === "approve" ? "Approve" : "Reject",
        inputValidator: (value) =>
          decision === "reject" && !String(value || "").trim()
            ? "Rejection remark is required."
            : undefined
      });

      if (!result.isConfirmed) return;

      const body = {
        decision,
        remark: String(result.value || "").trim()
      };

      if (kind === "amendments") {
        await CashEntryServices.reviewAmendment(record._id, body);
      } else {
        await CashEntryServices.reviewDateAccess(record._id, body);
      }

      setExpandedId("");
      setRefresh((value) => value + 1);
      onChanged?.();

      await Swal.fire(
        "Success",
        "Accounts review completed.",
        "success"
      );
    } catch (failure) {
      await Swal.fire("Error", cashError(failure), "error");
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  if (!canView) return null;

  return (
    <div
      className={
        standalone ? "w-100" : "border rounded p-3 mb-3 w-100"
      }
    >
      {!standalone && (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          disabled={busy}
          onClick={() => setOpen((value) => !value)}
        >
          Amendments / Date Access — Accounts
        </button>
      )}

      {visible && (
        <>
          {!isAccounts && kind === "amendments" && (
            <div
              className="d-flex flex-wrap gap-2 mb-3"
              role="group"
              aria-label="Amendment entry type"
            >
              <button
                type="button"
                className={`btn btn-sm ${entryType === "out"
                    ? "btn-danger"
                    : "btn-outline-danger"
                  }`}
                disabled={busy}
                aria-pressed={entryType === "out"}
                onClick={() => {
                  setEntryType("out");
                  setPage(1);
                  setExpandedId("");
                }}
              >
                Cash Out
              </button>

              <button
                type="button"
                className={`btn btn-sm ${entryType === "in"
                    ? "btn-success"
                    : "btn-outline-success"
                  }`}
                disabled={busy}
                aria-pressed={entryType === "in"}
                onClick={() => {
                  setEntryType("in");
                  setPage(1);
                  setExpandedId("");
                }}
              >
                Cash In
              </button>
            </div>
          )}

          <div className="row g-3 align-items-end mb-3 mt-1">
            <div className="col-12 col-md-4">
              <label className="form-label">Request Type</label>

              <select
                className="form-select"
                value={kind}
                disabled={busy}
                onChange={(event) => {
                  setKind(event.target.value);
                  setPage(1);
                  setExpandedId("");
                }}
              >
                <option value="amendments">Entry Amendments</option>
                <option value="dates">Date Access</option>
              </select>
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label">Status</label>

              <select
                className="form-select"
                value={status}
                disabled={busy}
                onChange={(event) => {
                  setStatus(event.target.value);
                  setPage(1);
                  setExpandedId("");
                }}
              >
                <option value="pendingAccounts">Pending Accounts</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label">Rows</label>

              <select
                className="form-select"
                value={limit}
                disabled={busy}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                  setPage(1);
                  setExpandedId("");
                }}
              >
                {[10, 20, 50, 100].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <WorkflowLoader />
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table
                  className="table table-bordered table-hover align-middle w-100 mb-0"
                  style={{ fontSize: 13 }}
                >
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Passbook / Store</th>
                      <th>Requested By</th>
                      <th>
                        {kind === "amendments" ? "Entry Type" : "Required Date"}
                      </th>
                      <th>
                        {kind === "amendments" ? "Old Amount" : "Access Expires"}
                      </th>
                      <th>
                        {kind === "amendments" ? "New Amount" : "Reason"}
                      </th>
                      {kind === "amendments" && <th>Balance Change</th>}
                      <th>Requested On</th>
                      <th>Status</th>
                      <th style={{ minWidth: 190 }}>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {!records.length && (
                      <tr>
                        <td
                          colSpan={columns}
                          className="text-center text-muted py-5"
                        >
                          No requests found.
                        </td>
                      </tr>
                    )}

                    {records.map((record, index) => (
                      <React.Fragment key={record._id}>
                        <tr>
                          <td>{(page - 1) * limit + index + 1}</td>

                          <td>
                            <div className="fw-semibold">
                              {record.passbook?.name || "—"}
                            </div>
                            <div className="small text-muted">
                              {record.store?.name ||
                                record.passbook?.store?.name ||
                                "—"}
                            </div>
                          </td>

                          <td>{record.requestedBy?.name || "—"}</td>

                          <td>
                            {kind === "amendments"
                              ? record.entryType === "in"
                                ? "Cash In"
                                : "Cash Out"
                              : record.allowedDate}
                          </td>

                          <td>
                            {kind === "amendments"
                              ? cashMoney(record.oldValues?.amount)
                              : cashDate(record.expiresAt)}
                          </td>

                          <td
                            style={{
                              maxWidth: 260,
                              overflowWrap: "anywhere"
                            }}
                          >
                            {kind === "amendments"
                              ? cashMoney(record.newValues?.amount)
                              : record.reason || "—"}
                          </td>

                          {kind === "amendments" && (
                            <td>{cashMoney(record.balanceChange)}</td>
                          )}

                          <td>{cashDate(record.createdAt)}</td>
                          <td><StatusBadge status={record.status} /></td>

                          <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                            <div className="d-flex justify-content-center align-items-center gap-1">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                disabled={busy}
                                title={expandedId === record._id ? "Hide details" : "View details"}
                                aria-label={
                                  expandedId === record._id ? "Hide details" : "View details"
                                }
                                aria-expanded={expandedId === record._id}
                                onClick={() =>
                                  setExpandedId((previous) =>
                                    previous === record._id ? "" : record._id
                                  )
                                }
                                style={{
                                  padding: "3px 7px",
                                  fontSize: "13px",
                                  lineHeight: 1.3
                                }}
                              >
                                <i className="bi bi-eye" aria-hidden="true" />
                              </button>

                             {isAccounts && record.status === "pendingAccounts" && (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-success"
                                    disabled={busy}
                                    title="Approve"
                                    aria-label="Approve"
                                    onClick={() => review(record, "approve")}
                                    style={{
                                      padding: "3px 7px",
                                      fontSize: "13px",
                                      lineHeight: 1.3
                                    }}
                                  >
                                    <i className="bi bi-hand-thumbs-up" aria-hidden="true" />
                                  </button>

                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    disabled={busy}
                                    title="Reject"
                                    aria-label="Reject"
                                    onClick={() => review(record, "reject")}
                                    style={{
                                      padding: "3px 7px",
                                      fontSize: "13px",
                                      lineHeight: 1.3
                                    }}
                                  >
                                    <i className="bi bi-hand-thumbs-down" aria-hidden="true" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>

                        {expandedId === record._id && (
                          <tr>
                            <td
                              colSpan={columns}
                              className="p-3"
                              style={{ background: "#f5f7fb" }}
                            >
                              {kind === "amendments" ? (
                                <AmendmentComparison record={record} />
                              ) : (
                                <div className="row g-3">
                                  <div className="col-md-4">
                                    <strong>Required Date</strong>
                                    <div>{record.allowedDate}</div>
                                  </div>
                                  <div className="col-md-4">
                                    <strong>Access Expires</strong>
                                    <div>{cashDate(record.expiresAt)}</div>
                                  </div>
                                  <div className="col-md-4">
                                    <strong>Reviewed By</strong>
                                    <div>{record.reviewedBy?.name || "—"}</div>
                                  </div>
                                  <div className="col-md-6">
                                    <strong>Request Reason</strong>
                                    <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                                      {record.reason || "—"}
                                    </div>
                                  </div>
                                  <div className="col-md-6">
                                    <strong>Accounts Remark</strong>
                                    <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                                      {record.reviewRemark || "—"}
                                    </div>
                                  </div>
                                  <div className="col-12 small text-muted">
                                    Reviewed On: {cashDate(record.reviewedAt)}
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pager
                page={page}
                total={total}
                limit={limit}
                disabled={loading || busy}
                onChange={setPage}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

const AmendmentEditor = ({
  entry,
  passbookId,
  onCancel,
  onSubmitted
}) => {
  const originalLocal = cashLocalDateTime(entry.dateTime);

  const [amount, setAmount] = useState(String(entry.amount ?? ""));
  const [date, setDate] = useState(originalLocal);
  const [remark, setRemark] = useState(entry.remark || "");
  const [reason, setReason] = useState("");
  const [retained, setRetained] = useState([...(entry.image || [])]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const lock = useRef(false);

  const {
    dateWindow,
    dateWindowError,
    reloadDateWindow
  } = useCashEntryDateWindow(passbookId);

  const changeFiles = (nextFiles) => {
    if (busy) return;

    const next = Array.from(nextFiles || []);
    const error = validateCashFiles(next, retained);

    if (error) {
      Swal.fire("Attachments", error, "warning");
      return;
    }

    setFiles(next);
  };

  const submit = async () => {
    if (lock.current) return;

    const value = Number(amount);

    if (
      !amount.trim() ||
      !Number.isFinite(value) ||
      value <= 0 ||
      Math.abs(value - Math.round(value * 100) / 100) > 0.0000001
    ) {
      Swal.fire(
        "Amount",
        "Enter a positive amount with up to two decimals.",
        "warning"
      );
      return;
    }

    if (!remark.trim() || !reason.trim()) {
      Swal.fire(
        "Required",
        "Entry remark and amendment reason are required.",
        "warning"
      );
      return;
    }

    if (!isAllowedCashDate(date, dateWindow, originalLocal)) {
      Swal.fire(
        "Date",
        "Select today/yesterday, keep original date or obtain Accounts access.",
        "warning"
      );
      return;
    }

    const fileError = validateCashFiles(files, retained);

    if (fileError) {
      Swal.fire("Attachments", fileError, "warning");
      return;
    }

    if (
      value === Number(entry.amount) &&
      date === originalLocal &&
      remark.trim() === String(entry.remark || "").trim() &&
      !files.length &&
      JSON.stringify(retained) === JSON.stringify(entry.image || [])
    ) {
      Swal.fire("No Changes", "Change at least one field.", "info");
      return;
    }

    lock.current = true;
    setBusy(true);

    try {
      let names = [];

      if (files.length) {
        const response = await CashEntryServices.uploadAttachments(files);
        const uploaded = readCashData(response)?.filenames;

        if (
          !Array.isArray(uploaded) ||
          uploaded.length !== files.length ||
          uploaded.some((name) => typeof name !== "string" || !name.trim())
        ) {
          throw new Error("Invalid attachment upload response.");
        }

        names = uploaded;
      }

      if (!isAllowedCashDate(date, dateWindow, originalLocal)) {
        throw new Error(
          "Date access has expired. Refresh access or retain the original date."
        );
      }

      await CashEntryServices.requestAmendment(entry._id, {
        reason: reason.trim(),
        newValues: {
          amount: value,
          dateTime:
            date === originalLocal
              ? entry.dateTime
              : new Date(`${date}:00+05:30`).toISOString(),
          remark: remark.trim(),
          image: [...retained, ...names]
        }
      });

      await Swal.fire(
        "Submitted",
        "Amendment sent to Accounts. Original entry remains unchanged.",
        "success"
      );

      onSubmitted();
    } catch (failure) {
      await Swal.fire("Error", cashError(failure), "error");
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  const difference = Number(amount) - Number(entry.amount);

  return (
    <div className="border rounded p-3 my-2">
      <h6>Request Amendment</h6>

      <div className="row g-2">
        <div className="col-md-6">
          <CashEntryDateField
            inputId={`amend-date-${entry._id}`}
            value={date}
            originalValue={originalLocal}
            dateWindow={dateWindow}
            disabled={busy}
            onChange={setDate}
          />
        </div>

        <div className="col-md-6">
          <label className="form-label">New Amount (₹)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            className="form-control"
            value={amount}
            disabled={busy}
            onWheel={(event) => event.currentTarget.blur()}
            onChange={(event) => setAmount(event.target.value)}
          />
          <small>
            Old: {cashMoney(entry.amount)}
            {" | "}Balance impact:{" "}
            {cashMoney(entry.entryType === "in" ? difference : -difference)}
          </small>
        </div>

        <div className="col-12">
          <label className="form-label">New Entry Remark</label>
          <textarea
            className="form-control"
            value={remark}
            disabled={busy}
            onChange={(event) => setRemark(event.target.value)}
          />
        </div>

        <div className="col-12">
          <label className="form-label">Amendment Reason</label>
          <textarea
            className="form-control"
            value={reason}
            disabled={busy}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
      </div>

      {dateWindowError && (
        <div className="alert alert-warning my-2">
          {dateWindowError} Original date can still be retained.
        </div>
      )}

      <div className="my-3">
        <strong>Retained attachments</strong>
        <FileLinks
          files={retained}
          disabled={busy}
          onRemove={(index) =>
            setRetained((previous) =>
              previous.filter((_, i) => i !== index)
            )
          }
        />
      </div>

      <CashEntryAttachments
        inputId={`amend-files-${entry._id}`}
        attachments={files}
        disabled={busy}
        onChange={changeFiles}
      />

      <div className="small text-muted my-2">
        Limit includes retained attachments. Remove an existing file
        before replacing it. Images are compressed by the upload API.
      </div>

      <CashDateAccessPanel
        passbookId={passbookId}
        disabled={busy}
        onRefresh={reloadDateWindow}
      />

      <div className="d-flex gap-2">
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={busy}
          onClick={submit}
        >
          {busy ? "Submitting..." : "Submit to Accounts"}
        </button>

        <button
          type="button"
          className="btn btn-sm btn-secondary"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel Amendment
        </button>
      </div>
    </div>
  );
};

export const CashEntryAmendmentPanel = ({
  entry,
  passbook,
  onChanged
}) => {
  const [editing, setEditing] = useState(false);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  const role = cashRole();
  const id = cashId(entry);
  const passbookId = cashId(entry?.passbook) || cashId(passbook);
  const allowed = role === "cashmaker" || role === "accounts";
  const limit = 20;

  useEffect(() => {
    setEditing(false);
    setSubmitted(false);
    setPage(1);
  }, [id]);

  useEffect(() => {
    if (!id || !allowed) return;

    let cancelled = false;

    setLoading(true);
    setError("");
    setRecords([]);

    CashEntryServices.getAmendments({ entryId: id, page, limit })
      .then((response) => {
        if (cancelled) return;

        const data = readCashData(response);

        if (!Array.isArray(data?.amendments)) {
          throw new Error("Invalid amendment response.");
        }

        const count = Number(data.total || 0);
        const lastPage = Math.max(1, Math.ceil(count / limit));

        setTotal(count);

        if (page > lastPage) {
          setPage(lastPage);
          return;
        }

        setRecords(data.amendments);
        setSubmitted(false);
      })
      .catch((failure) => {
        if (!cancelled) {
          setTotal(0);
          setError(cashError(failure));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, allowed, page, refresh]);

  if (!entry || !allowed) return null;

  const pending =
    Boolean(entry.pendingAmendment) ||
    submitted ||
    records.some((record) => record.status === "pendingAccounts");

  const canRequest =
    role === "cashmaker" &&
    entry.status === "approved" &&
    Boolean(entry.postedAt) &&
    passbookId &&
    !pending &&
    !loading &&
    !error;

  return (
    <div className="border rounded p-3 mt-3 w-100">
      <h6>Entry Amendments</h6>

      {pending && (
        <div className="alert alert-info py-2">
          An amendment is pending Accounts approval.
        </div>
      )}

      {!editing && canRequest && (
        <button
          type="button"
          className="btn btn-sm btn-primary mb-2"
          onClick={() => setEditing(true)}
        >
          Request Amendment
        </button>
      )}

      <button
        type="button"
        className="btn btn-sm btn-outline-secondary ms-2 mb-2"
        disabled={loading || editing}
        onClick={() => {
          setRefresh((value) => value + 1);
          onChanged?.();
        }}
      >
        Refresh History
      </button>

      {editing && (
        <AmendmentEditor
          key={id}
          entry={entry}
          passbookId={passbookId}
          onCancel={() => setEditing(false)}
          onSubmitted={() => {
            setEditing(false);
            setSubmitted(true);
            setPage(1);
            setRefresh((value) => value + 1);
            onChanged?.();
          }}
        />
      )}

      {loading ? (
        <WorkflowLoader />
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <>
          {records.map((record) => (
            <details
              key={record._id}
              className="border rounded p-3 mb-2"
            >
              <summary>
                <StatusBadge status={record.status} />
                {" | "}
                {cashDate(record.createdAt)}
                {" | "}
                {cashMoney(record.newValues?.amount)}
              </summary>

              <div className="mt-3">
                <AmendmentComparison record={record} />
              </div>
            </details>
          ))}

          {!records.length && (
            <div className="text-muted py-3">
              No amendment history.
            </div>
          )}

          <Pager
            page={page}
            total={total}
            limit={limit}
            disabled={loading || editing}
            onChange={setPage}
          />
        </>
      )}
    </div>
  );
};